import { useCollectionView } from './tablePreferences';
import { useTablePresentation } from './TablePresentation';
import './table-standard.css';
import { Modal } from './Modal';
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { PaginationBar } from './Pagination';
import type { PageSize } from '@/lib/usePagedList';
import { ArrowDown, ArrowUp, ArrowUpDown, LayoutGrid, List, Table2, Maximize2, Minimize2 } from 'lucide-react';
import './record-grid.css';
import { AnimatePresence, motion } from 'motion/react';
import { cx } from '@/lib/cx';
import { Button } from './Button';
import { Skeleton } from './Misc';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /** Label shown beside the value in the stacked mobile card (defaults to header if string). */
  mobileLabel?: string;
  /** Hide in the stacked mobile card. */
  hideOnMobile?: boolean;
  /** This column is the card title on mobile (full width, no label). */
  primary?: boolean;
  /** Keep supporting information in the expandable Details section. */
  detailOnly?: boolean;
}

export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: string;
  dir: SortDir;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  sort?: SortState | null;
  onSortChange?: (s: SortState) => void;
  /** Called when a row is activated (click anywhere / Enter). Links inside cells keep working. */
  onRowClick?: (row: T) => void;
  onDetails?: (row: T) => void;
  detailsMode?: 'inline' | 'dialog';
  empty?: ReactNode;
  caption: string;
  skeletonRows?: number;
  rowClassName?: (row: T) => string | undefined;
  stickyHeader?: boolean;
  /** For endpoints that return the complete collection, paginate locally. */
  paginate?: boolean;
  /** Initial view; every collection offers Grid, List and Table. */
  defaultView?: 'grid' | 'list' | 'table';
  /** Controlled by a page that already has its own view switch. */
  view?: 'grid' | 'list' | 'table';
}

/**
 * Data table: semantic <table>, sortable headers with aria-sort, sticky header, and a
 * stacked card layout below 768px (same DOM, CSS-driven, so no duplicate content).
 */
export function DataTable<T>({ columns, rows, rowKey, loading, sort, onSortChange, onRowClick, onDetails: suppliedDetails, detailsMode = 'dialog', empty, caption, skeletonRows = 6, rowClassName, stickyHeader = true, paginate = false, defaultView, view: controlledView }: DataTableProps<T>) {
  const [savedView, setView] = useCollectionView(caption, defaultView ?? 'list');
  const view = controlledView ?? savedView;
  const [fullscreen, setFullscreen] = useState(false);
  const fullscreenButton = useRef<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<T | null>(null);
  const onDetails = suppliedDetails ?? (detailsMode === 'dialog' ? (row: T) => setDetailRow(row) : undefined);
  const currentDetail = detailRow ? rows?.find(row => rowKey(row) === rowKey(detailRow)) ?? detailRow : null;
  // Show every column that fits the available width. Only when the table is too narrow do the
  // lowest-priority facts move into a readable detail row; identity and actions always stay visible.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => setWidth(Math.round(entries[0]?.contentRect.width ?? el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [view, fullscreen]);
  const actionColumn = columns.find(c => ['actions', 'action', 'open', 'act', 'edit', 'manage', 'download'].includes(c.key));
  const presentation = useTablePresentation(caption, columns, actionColumn?.key);
  const dataColumns = columns.filter(c => c !== actionColumn);
  const priority = (c: Column<T>) => c.primary || ['select', 'name', 'target', 'user', 'tenant'].includes(c.key) ? 0 : ['status', 'state', 'health', 'plan', 'role', 'amount', 'email', 'manager', 'org', 'price'].includes(c.key) ? 1 : 2;
  // Unmeasured (e.g. server render / tests): the historical limit of five columns. Stacked mobile
  // cards (< 768px) have room for every fact, so nothing is hidden there.
  const stacked = typeof window !== 'undefined' && window.innerWidth < 768;
  const capacity = width === 0 ? 5 : stacked ? Number.POSITIVE_INFINITY : Math.max(4, Math.floor(width / 180));
  const fits = columns.length <= capacity && !columns.some(c => c.detailOnly);
  const dataSlots = Math.max(2, capacity - (actionColumn ? 1 : 0) - 1);
  const preferred = dataColumns.filter(c => !c.detailOnly).sort((a,b) => priority(a) - priority(b)).slice(0, width === 0 ? (actionColumn ? 3 : 4) : dataSlots);
  const essentials = dataColumns.filter(c => preferred.includes(c));
  const summaryColumns = view === 'table' ? presentation.columns : fits ? columns : [...essentials, ...(actionColumn ? [actionColumn] : [])];
  const detailColumns = view === 'table' ? [] : columns.filter(c => !summaryColumns.includes(c));
  const [pageSize, setPageSize] = useState<PageSize>(10);
  const [page, setPage] = useState(1);
  const rowIds = rows?.map(rowKey).join('|') ?? '';
  useEffect(() => { setPage(1); }, [rowIds, pageSize, sort?.key, sort?.dir]);
  const visibleRows = paginate ? rows?.slice((page - 1) * pageSize, page * pageSize) : rows;
  const showEmpty = !loading && rows && rows.length === 0;

  const onRowActivate = (e: MouseEvent | KeyboardEvent, row: T) => {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    if (target.closest('a,button,input,select,textarea,[role="menuitem"],[data-no-row-click]')) return;
    if ('key' in e && e.key !== 'Enter') return;
    onRowClick(row);
  };

  const content = (
    <>
    {detailsMode === 'dialog' && <Modal open={!!currentDetail} onOpenChange={open => !open && setDetailRow(null)} title={`${caption} details`} size="lg" footer={<Button onClick={() => setDetailRow(null)}>Close</Button>}>
      {currentDetail && <><dl className="table-detail-grid">{dataColumns.map(column => <div key={column.key}><dt>{column.header}</dt><dd>{column.cell(currentDetail)}</dd></div>)}</dl>{actionColumn && <div className="record-card-actions">{actionColumn.cell(currentDetail)}</div>}</>}
    </Modal>}
    <div className="collection-toolbar"><span>{caption}</span><div className="table-presentation-controls">
      {view === 'table' && <>{presentation.controls}<Button ref={fullscreenButton} size="sm" variant="ghost" aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} icon={fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />} onClick={() => setFullscreen(!fullscreen)} /></>}
      {!controlledView && <div className="collection-switch" role="group" aria-label={`${caption} view`}>{(['grid', 'list', 'table'] as const).map(mode => <Button key={mode} size="sm" variant={view === mode ? 'secondary' : 'ghost'} aria-pressed={view === mode} icon={mode === 'grid' ? <LayoutGrid size={16} /> : mode === 'table' ? <Table2 size={16} /> : <List size={16} />} onClick={() => setView(mode)}>{mode === 'grid' ? 'Grid' : mode === 'table' ? 'Table' : 'List'}</Button>)}</div>}
    </div></div>
    {view === 'grid' ? <div className="record-grid" role="list" aria-label={caption} aria-busy={!!loading}>
      {loading ? Array.from({ length: 6 }, (_, index) => <div className="record-card" key={index} aria-hidden><Skeleton height={24} /><Skeleton height={72} /></div>) : visibleRows?.map(row => {
        const title = dataColumns.find(c => c.primary) ?? dataColumns[0];
        const facts = dataColumns.filter(c => c !== title).sort((a, b) => (['status', 'state', 'due'].includes(a.key) ? 0 : 1) - (['status', 'state', 'due'].includes(b.key) ? 0 : 1));
        const renderFacts = (cols: Column<T>[]) => <dl className="record-facts">{cols.map(c => <div key={c.key}><dt>{c.mobileLabel ?? c.header}</dt><dd>{c.cell(row)}</dd></div>)}</dl>;
        return <article role="listitem" className="record-card" key={rowKey(row)}>
          <div className="record-card-title">{title?.cell(row)}</div>
          {renderFacts(facts.slice(0, 4))}
          {!onDetails && facts.length > 4 && <details className="record-more"><summary>More information</summary>{renderFacts(facts.slice(4))}</details>}
          {(actionColumn || onDetails || onRowClick) && <div className="record-card-actions">{onRowClick && <Button size="sm" variant="secondary" onClick={() => onRowClick(row)}>Open</Button>}{onDetails && <Button size="sm" variant="secondary" onClick={() => onDetails(row)}>Details</Button>}{actionColumn?.cell(row)}</div>}
        </article>;
      })}
      {showEmpty && <div className="record-empty">{empty}</div>}
    </div> :
    <div ref={wrapRef} className={cx('table-wrap', view === 'table' ? 'table-standard' : 'table-fit', stickyHeader && 'is-sticky')} data-density={presentation.density} data-columns={summaryColumns.length} role="region" aria-label={`${caption} records`} tabIndex={view === 'table' ? 0 : undefined} aria-busy={!!loading}>
      <table className={cx('table', onRowClick && 'is-interactive')} style={view === 'table' ? { width: 56 + summaryColumns.reduce((n, c) => n + presentation.columnWidth(c), 0) } : undefined}>
        <caption className="sr-only">{caption}</caption>
        {view === 'table' && <colgroup><col style={{ width: 56 }} />{summaryColumns.map(c => <col key={c.key} style={{ width: presentation.columnWidth(c) }} />)}</colgroup>}
        <thead>
          <tr>
            {view === 'table' && <th scope="col" className="table-row-number" title="Row on this page">#</th>}
            {summaryColumns.map((c) => {
              const active = sort?.key === c.key;
              const ariaSort = c.sortable ? (active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined;
              return (
                <th key={c.key} scope="col" aria-label={typeof c.header === 'string' ? c.header : c.mobileLabel} style={{ textAlign: c.align, ...(view === 'table' ? presentation.style(c) : { width: c.width }) }} aria-sort={ariaSort} className={cx(c === actionColumn && 'table-actions-cell', c.primary && 'table-identity-cell')}>
                  {c.sortable && onSortChange ? (
                    <button type="button" className={cx('th-sort', active && 'is-active')} onClick={() => onSortChange({ key: c.key, dir: active && sort!.dir === 'asc' ? 'desc' : 'asc' })}>
                      <span>{c.header}</span>
                      {active ? sort!.dir === 'asc' ? <ArrowUp size={13} aria-hidden /> : <ArrowDown size={13} aria-hidden /> : <ArrowUpDown size={13} aria-hidden className="th-sort-idle" />}
                    </button>
                  ) : (
                    <span className="table-header-label">{c === actionColumn ? 'Actions' : c.header}</span>
                  )}
                  {view === 'table' && presentation.headerControls(c)}
                </th>
              );
            })}
            {detailColumns.length > 0 && <th scope="col" className="table-detail-toggle">Details</th>}
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: skeletonRows }, (_, i) => (
              <tr key={`sk-${i}`} className="is-skeleton" aria-hidden>
                {view === 'table' && <td className="table-row-number"><Skeleton width="24px" /></td>}
                {summaryColumns.map((c, ci) => (
                  <td key={c.key} className={cx(c.primary && 'td-primary', c.hideOnMobile && 'hide-mobile')}>
                    <Skeleton width={ci === 0 ? '72%' : `${35 + ((i * 11 + ci * 17) % 45)}%`} />
                  </td>
                ))}
                {detailColumns.length > 0 && <td><Skeleton width="60%" /></td>}
              </tr>
            ))}
          <AnimatePresence initial={false}>
            {!loading &&
              visibleRows?.map((row, rowIndex) => (
                <Fragment key={rowKey(row)}>
                <motion.tr
                  key={rowKey(row)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.16 }}
                  className={rowClassName?.(row)}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(e) => onRowActivate(e, row)}
                  onClick={(e) => onRowActivate(e, row)}
                >
                  {view === 'table' && <td className="table-row-number">{String((paginate ? (page - 1) * pageSize : 0) + rowIndex + 1).padStart(2, '0')}</td>}
                  {summaryColumns.map((c) => (
                    <td
                      key={c.key}
                      style={{ textAlign: c.align, ...(view === 'table' ? presentation.style(c) : {}) }}
                      data-label={c.primary ? undefined : (c.mobileLabel ?? (typeof c.header === 'string' ? c.header : undefined))}
                      className={cx(c.primary && 'td-primary', c === actionColumn && 'table-actions-cell', c.align === 'right' && 'td-right')}
                    >
                      {view === 'table' && c !== actionColumn && c.key !== 'select' ? <div className="table-cell-content" tabIndex={0} ref={el => { if (el) el.title = el.textContent ?? ''; }}>{c.cell(row)}</div> : c.cell(row)}
                    </td>
                  ))}
                  {detailColumns.length > 0 && <td className="table-detail-toggle" data-label="More information"><Button size="sm" variant="secondary" aria-expanded={onDetails ? undefined : expanded === rowKey(row)} aria-label={`${expanded === rowKey(row) ? 'Close' : 'Show'} details for row ${rowKey(row)}`} onClick={() => onDetails ? onDetails(row) : setExpanded(expanded === rowKey(row) ? null : rowKey(row))}>{expanded === rowKey(row) ? 'Close' : 'Details'}</Button></td>}
                </motion.tr>
                {!onDetails && expanded === rowKey(row) && detailColumns.length > 0 && <tr className="table-detail-row"><td colSpan={summaryColumns.length + 1}><dl className="table-detail-grid">{detailColumns.map(c => <div key={c.key}><dt>{c.header}{c.sortable && onSortChange && <Button size="sm" variant="ghost" onClick={() => onSortChange({ key: c.key, dir: sort?.key === c.key && sort.dir === 'asc' ? 'desc' : 'asc' })}>Sort</Button>}</dt><dd>{c.cell(row)}</dd></div>)}</dl></td></tr>}
                </Fragment>
              ))}
          </AnimatePresence>
        </tbody>
      </table>
      {showEmpty && <div className="table-empty">{empty}</div>}
    </div>}
    {paginate && !loading && <PaginationBar page={page} pageSize={pageSize} onPageSize={setPageSize} hasPrev={page > 1} hasNext={page * pageSize < (rows?.length ?? 0)} onPrev={() => setPage(p => Math.max(1, p - 1))} onNext={() => setPage(p => p + 1)} shown={visibleRows?.length ?? 0} total={rows?.length ?? 0} noun={caption.toLowerCase()} />}
    </>
  );
  return fullscreen ? <Modal open onOpenChange={open => { setFullscreen(open); if (!open) requestAnimationFrame(() => fullscreenButton.current?.focus()); }} title={caption} className="table-fullscreen">{content}</Modal> : content;
}
